/**
 * Socket Print Agent Installation Wizard Dialog
 * 
 * Shows step-by-step instructions for installing ClientPrintAgent
 * when user enables "Socket Print Mode" and the agent isn't running.
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stepper, Step, StepLabel, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { isSocketPrintAvailable, getSocketPrinters } from './socketPrintService';

const INSTALLER_ZIP = '/downloads/ClientPrintAgent.zip';
const steps = ['Download Agent', 'Extract & Install', 'Verify Connection'];

const SocketPrintInstallDialog = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && activeStep === 2) {
      checkConnection();
    }
  }, [open, activeStep]);

  const checkConnection = async () => {
    setIsChecking(true);
    setError(null);
    try {
      const available = await isSocketPrintAvailable();
      setIsConnected(available);
      if (available) {
        const printerList = await getSocketPrinters();
        setPrinters(printerList || []);
      }
    } catch (err) {
      setError(err.message);
      setIsConnected(false);
    } finally {
      setIsChecking(false);
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      if (isConnected) {
        onSuccess(printers);
        onClose();
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = INSTALLER_ZIP;
    link.download = 'ClientPrintAgent.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setActiveStep(0);
    setIsConnected(false);
    setPrinters([]);
    setError(null);
    onClose();
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <DownloadIcon sx={{ fontSize: 60, color: '#1976d2', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 16px 0' }}>Download ClientPrintAgent</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              ClientPrintAgent enables silent printing via Socket.IO. 
              Download the installer package below.
            </p>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ textTransform: 'none' }}
            >
              Download ClientPrintAgent.zip
            </Button>
            <p style={{ color: '#999', fontSize: '12px', marginTop: '16px' }}>
              Contains: Print server + auto-installer (setup.bat)
            </p>
          </div>
        );

      case 1:
        return (
          <div style={{ padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Extract & Install</h3>
            <ol style={{ color: '#666', lineHeight: '2', paddingLeft: '20px' }}>
              <li>
                <strong>Extract the zip</strong> to <code>C:\ClientPrintAgent</code>
                <ul style={{ marginTop: '5px', color: '#888' }}>
                  <li>Right-click the zip → "Extract All"</li>
                  <li>Set destination to <code>C:\ClientPrintAgent</code></li>
                </ul>
              </li>
              <li>
                <strong>Run <code>setup.bat</code> as Administrator</strong>
                <ul style={{ marginTop: '5px', color: '#888' }}>
                  <li>Right-click <code>setup.bat</code> → "Run as administrator"</li>
                  <li>This will automatically:</li>
                  <li style={{ listStyle: 'none', paddingLeft: '10px' }}>✅ Install Node.js (if not installed)</li>
                  <li style={{ listStyle: 'none', paddingLeft: '10px' }}>✅ Install all dependencies & Puppeteer</li>
                  <li style={{ listStyle: 'none', paddingLeft: '10px' }}>✅ Register as Windows service (auto-start on boot)</li>
                  <li style={{ listStyle: 'none', paddingLeft: '10px' }}>✅ Start the print agent on port 5000</li>
                </ul>
              </li>
              <li>
                <strong>Wait for "SUCCESS" message</strong> in the command window
              </li>
            </ol>
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              padding: '12px', 
              borderRadius: '4px', 
              marginTop: '16px',
              border: '1px solid #90caf9'
            }}>
              <strong>💡 One-time setup:</strong> After installation, the print agent starts automatically on every boot. No manual steps needed.
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {isChecking ? (
              <>
                <CircularProgress size={60} sx={{ marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0' }}>Checking Connection...</h3>
                <p style={{ color: '#666' }}>Looking for ClientPrintAgent on port 5000...</p>
              </>
            ) : isConnected ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#4caf50' }}>Connected Successfully!</h3>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  ClientPrintAgent is running. Silent printing is ready.
                </p>
                {printers.length > 0 && (
                  <div style={{ 
                    backgroundColor: '#e8f5e9', 
                    padding: '12px', 
                    borderRadius: '4px',
                    textAlign: 'left'
                  }}>
                    <strong>🖨️ Available Printers ({printers.length}):</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      {printers.slice(0, 5).map((printer, i) => (
                        <li key={i} style={{ color: '#666' }}>{printer}</li>
                      ))}
                      {printers.length > 5 && (
                        <li style={{ color: '#999' }}>...and {printers.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <ErrorIcon sx={{ fontSize: 60, color: '#f44336', marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#f44336' }}>Connection Failed</h3>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  Could not connect to ClientPrintAgent. Please make sure:
                </p>
                <ul style={{ textAlign: 'left', color: '#666', display: 'inline-block' }}>
                  <li>You extracted files to <code>C:\ClientPrintAgent</code></li>
                  <li>You ran <code>setup.bat</code> as Administrator</li>
                  <li>The setup completed with "SUCCESS" message</li>
                  <li>Port 5000 is not blocked by firewall</li>
                </ul>
                {error && (
                  <p style={{ color: '#f44336', fontSize: '12px', marginTop: '12px' }}>
                    Error: {error}
                  </p>
                )}
                <div style={{ marginTop: '16px' }}>
                  <Button
                    variant="outlined"
                    onClick={checkConnection}
                    startIcon={<PrintIcon />}
                  >
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', paddingBottom: 0 }}>
        <PrintIcon sx={{ fontSize: 32, color: '#1976d2', marginRight: '8px', verticalAlign: 'middle' }} />
        Setup Socket Print Agent
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ padding: '20px 0' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <div style={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={activeStep === 2 && !isConnected}
        >
          {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SocketPrintInstallDialog;
